<template>
  <BContainer
    v-if="currentUser"
    class="mb-4"
  >
    <div class="d-flex justify-content-end">
      <BBreadcrumb
        v-if="$route.meta.breadcrumb"
        :items="$route.meta.breadcrumb || []"
      />
    </div>

    <BRow
      class="py-2"
    >
      <BCol
        cols="12"
        md="8"
      >
        <BFormGroup
          label="Search"
          label-for="filterSearch"
          label-class="font-weight-bolder"
          class="mb-2"
        >
          <BFormInput
            id="filterSearch"
            v-model="tableProvinces.filter.search"
            placeholder="search here"
            autocomplete="off"
            debounce="1000"
            type="text"
          />
        </BFormGroup>
      </BCol>
      <BCol
        cols="12"
        md="4"
      >
        <BFormGroup
          label="Region"
          label-for="filterRegion"
          label-class="font-weight-bolder"
          class="mb-2"
        >
          <VSelect
            v-model="tableProvinces.selected.region"
            placeholder="filter region here"
            input-id="filterRegion"
            class="vs-white"
            label="region_name"
            clearable
            append-to-body
            :options="(tableProvinces.options.regions)"
            :loading="(tableProvinces.fetching.regions)"
            :disabled="(tableProvinces.busy || tableProvinces.fetching.regions)"
            :calculate-position="calculateDropPosition"
            @search="searchFilterRegions"
          >
            <template #option="{ region_name }">
              <div
                class="text-break text-wrap"
                v-text="region_name"
              />
            </template>
            <template #selected-option="{ region_name }">
              <div
                class="w-225px w-md-100px w-lg-200px text-truncate"
                v-text="region_name"
              />
            </template>
            <template #no-options="">
              no available region
            </template>
          </VSelect>
        </BFormGroup>
      </BCol>
    </BRow>

    <CustomTable
      ref="tableProvinces"
      :provider="tableProvider"
      :fields="tableProvinces.fields"
      :filter="tableProvinces.filter"
      :filter-on="tableProvinces.filterOn"
      :sort-by="tableProvinces.sortBy"
      :sort-desc="tableProvinces.sortDesc"
      :sort-direction="tableProvinces.sortDirection"
      :total-rows="tableProvinces.totalRows"
      :current-page="tableProvinces.currentPage"
      :per-page="tableProvinces.perPage"
      :stacked="tableProvinces.stacked"
      :busy="tableProvinces.busy"
    >
      <template #cell(action)="{ item }">
        <div class="text-md-nowrap d-flex flex-column flex-md-row justify-content-center">
          <BButton
            size="sm"
            :disabled="tableProvinces.busy"
            :variant="Number(item.active) === 1 ? 'danger' : 'primary'"
            @click.prevent="updateProvince(item)"
          >
            {{ Number(item.active) === 1 ? 'Deactivate' : 'Activate' }}
          </BButton>
        </div>
      </template>
    </CustomTable>

    <BModal
      ref="modalProvince"
      scrollable
      no-close-on-esc
      no-enforce-focus
      no-close-on-backdrop
      aria-hidden="false"
      role="dialog"
      :title="MProvinceTitle"
    >
      <ValidationObserver
        ref="formProvince"
      >
        <form
          role="form"
          novalidate
          @submit.prevent
        >
          <BRow>
            <BCol
              cols="12"
            >
              <ValidationProvider
                v-slot="{ errors }"
                name="province name"
                vid="province_name"
                rules="required|max:150"
              >
                <BFormGroup
                  label="Province Name"
                  label-for="fProvinceName"
                  label-class="font-weight-bolder"
                  class="mb-2"
                >
                  <BFormInput
                    id="fProvinceName"
                    v-model="formProvince.province_name"
                    type="text"
                    maxlength="150"
                    autocomplete="off"
                    placeholder="enter province name here"
                    :state="errors.length > 0 ? false : null"
                  />
                  <div
                    v-if="(errors.length > 0)"
                    class="invalid-feedback"
                    v-text="errors[0]"
                  />
                </BFormGroup>
              </ValidationProvider>
            </BCol>

            <BCol
              cols="12"
            >
              <ValidationProvider
                v-slot="{ errors }"
                vid="region"
                name="region"
                rules="required"
              >
                <BFormGroup
                  label="Region"
                  label-for="fRegion"
                  label-class="font-weight-bolder"
                  class="mb-2"
                >
                  <VSelect
                    v-model="selected.region"
                    input-id="fRegion"
                    label="region_name"
                    append-to-body
                    placeholder="search region here"
                    :options="options.regions"
                    :disabled="fetching.regions"
                    :loading="fetching.regions"
                    :calculate-position="calculateDropPosition"
                    :class="{ 'is-invalid': errors.length > 0 }"
                    @search="searchRegions"
                  >
                    <template #no-options="">
                      no available region
                    </template>
                  </VSelect>
                  <div
                    v-if="(errors.length > 0)"
                    class="invalid-feedback"
                    v-text="errors[0]"
                  />
                </BFormGroup>
              </ValidationProvider>
            </BCol>

            <BCol
              v-if="state.editing"
              cols="12"
            >
              <BFormGroup>
                <BFormCheckbox
                  id="fActive"
                  v-model="formProvince.active"
                  switch
                  value="1"
                  name="active"
                  unchecked-value="0"
                  :disabled="state.busy"
                >
                  Is Active?
                </BFormCheckbox>
              </BFormGroup>
            </BCol>
          </BRow>

        </form>
      </ValidationObserver>

      <template #modal-footer="{cancel}">
        <BButton
          variant="primary"
          :disabled="state.busy"
          @click.prevent="validateProvince"
        >
          {{ state.editing ? 'Update Province' : 'Create Province' }}
        </BButton>
        <BButton
          variant="outline-dark"
          :disabled="state.busy"
          @click.prevent="discardBtnEvent => preventAriaHidden(discardBtnEvent, cancel)"
        >
          Discard
        </BButton>
      </template>

    </BModal>
  </BContainer>
</template>

<script>
import { mapGetters } from 'vuex'
import QUERY from 'lodash'

import MxMisc from '@/mixins/misc'
import MxFormatter from '@/mixins/formatter'
import MRegions from '@/mixins/locations/regions'

import { SAdminProvince } from '@/services/admin'

export default {
  name: 'VAdminProvinces',

  mixins: [
    MRegions,
    MxMisc,
    MxFormatter
  ],

  middleware: [
    'auth',
    'admin'
  ],

  metaInfo: () => ({
    title: 'Provinces'
  }),

  data () {
    return {
      state: {
        busy: false,
        editing: false
      },
      formProvince: {
        id: '',
        region: '',
        province_name: '',
        active: 1
      },
      tableProvinces: {
        busy: false,
        fetching: {
          regions: false
        },
        options: {
          regions: []
        },
        selected: {
          region: ''
        },
        filter: {
          search: '',
          region: ''
        },
        filterOn: [],
        stacked: 'sm',
        sortBy: 'id',
        sortDesc: false,
        sortDirection: 'asc',
        pageOptions: [10, 25, 50, 100],
        currentPage: 1,
        totalRows: 0,
        perPage: 10,
        fields: [
          { mobile: 0, key: 'index', label: '#', class: 'text-center' },
          { mobile: 5, key: 'action', class: 'text-center w-md-125px' },
          { mobile: 1, key: 'province_name', class: 'min-w-175px' },
          { mobile: 2, key: 'region.region_name', label: 'Region', class: 'min-w-175px' },
          { mobile: 3, key: 'active', class: 'text-md-center w-md-75px', formatter: this.yesOrNo },
          { mobile: 4, key: 'updated_at', class: 'min-w-150px', formatter: this.dateTimeShortFormatter, sortable: true }
        ].sort(this.scaleOnMobile)
      }
    }
  },

  computed: Object.assign({
    MProvinceTitle () {
      return this.state.editing ? 'Edit Province' : 'Create Province'
    }
  }, mapGetters({
    currentUser: 'auth/user'
  })),

  watch: {
    'selected.region' (region) {
      this.formProvince.region = region?.id || ''
    },
    'tableProvinces.selected.region' (region) {
      this.tableProvinces.filter.region = region?.id || ''
    }
  },

  mounted () {
    if (this.currentUser) {
      this.getRegions(this.tableProvinces)
    }
  },

  methods: {

    async tableProvider ({ currentPage, perPage, sortBy, sortDesc, filter }) {
      this.tableProvinces.busy = true
      const payload = {
        page: currentPage,
        per_page: perPage,
        sort: sortBy,
        sort_desc: sortDesc,
        filter_text: filter.search,
        region: filter.region
      }

      return await SAdminProvince.get(payload).then(
        ({ data }) => {
          this.tableProvinces.totalRows = data.total_rows
          return data.items
        }
      ).catch(() => {
        this.tableProvinces.totalRows = 0
        return []
      }).finally(() => {
        this.tableProvinces.busy = false
      })
    },

    async searchFilterRegions (search, loading) {
      this.searchRegionsProvider(this.tableProvinces, search, loading)
    },

    createProvince () {
      this.state.editing = false
      this.formProvince = Object.assign(this.formProvince, {
        id: '',
        region: '',
        province_name: '',
        active: 1
      })

      this.getRegions(this)

      this.$refs.modalProvince.show()
    },

    editProvince (province) {
      this.state.editing = true
      this.formProvince = Object.assign(this.formProvince, {
        id: province.id,
        region: province.region_id,
        province_name: province.province_name,
        active: province.active
      })

      this.getRegions(this).finally(() => {
        this.selected.region = QUERY.find(this.options.regions, {
          id: province.region_id
        })
      })

      this.$refs.modalProvince.show()
    },

    async updateProvince (province) {
      const isActive = Number(province.active) === 1

      this.formProvince = Object.assign(this.formProvince, {
        id: province.id,
        region: province.region_id,
        province_name: province.province_name,
        active: isActive ? 0 : 1
      })

      this.swalConfirm({
        html: isActive ? '<h6>Deactivate this Province?</h6>' : '<h6>Activate this Province?</h6>',
        confirmButtonText: isActive ? 'Deactivate' : 'Activate',
        customClass: {
          confirmButton: isActive ? 'btn btn-sm btn-danger' : 'btn btn-sm btn-primary'
        },
        preConfirm: async () => {
          this.state.busy = true
          return this.putProvince().finally(() => {
            this.state.busy = false
          })
        }
      })
    },

    async validateProvince () {
      await this.$refs.formProvince.validate().then(
        async validProvince => {
          if (!validProvince) {
            return this.swalInvalid()
          }

          this.swalConfirm({
            html: this.state.editing ? '<h6>Update this Province?</h6>' : '<h6>Create this Province?</h6>',
            confirmButtonText: this.state.editing ? 'Update' : 'Create',
            preConfirm: async () => {
              this.state.busy = true

              const createOrUpdate = async () => {
                return this.state.editing ? this.putProvince() : this.postProvince()
              }

              return createOrUpdate().catch(
                ({ message }) => {
                  this.$refs.formProvince.setErrors(message.errors)
                }
              ).finally(() => {
                this.state.busy = false
              })
            }
          }).then(({ value, isConfirmed }) => {
            if (value && isConfirmed) {
              this.state.busy = false
            }
          })
        }
      )
    },

    async postProvince () {
      return await SAdminProvince.post(this.formProvince).then(
        ({ data: { message } }) => {
          this.swalSuccess(message).then(() => {
            this.$refs.modalProvince.hide()
            this.$refs.tableProvinces.refetch()
          })
        }
      )
    },

    async putProvince () {
      return await SAdminProvince.put(this.formProvince).then(
        ({ data: { message, province } }) => {
          this.swalSuccess(message).then(() => {
            this.$refs.modalProvince.hide()
            this.$refs.tableProvinces.updateRow({
              id: province.id,
              region: province.region,
              region_id: province.region_id,
              province_name: province.province_name,
              active: province.active,
              updated_at: province.updated_at
            })
          })
        }
      )
    }
  }
}
</script>
